// @flow

import { useRef, useState } from 'react';
import { toastr } from 'react-redux-toastr';
import {
  useTableFilterSortData,
  useTableFilterSortSearchManager,
  useTableManageColumns,
} from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import useGenerate59AMutation from '../../../../hooks/data-modifying/useGenerate59AMutation';
import useIntl from '../../../../hooks/useIntl';
import { extractErrorMessage } from '../../../../utils/errors';
import useModalProps from '../../../../hooks/useModalProps';
import useSelectedProperty from '../../../../hooks/useSelectedProperty';
import useUniqueTableName from '../../../../hooks/useUniqueTableName';
import useCancelGrossRentCertificationAction from '../hooks.useCancelGrossRentCertificationAction';
import useEditGrossRentCertificationMutation from '../hooks.useEditGrossRentCertificationMutation';
import type { GrossRentCertification } from '../types';
import generalMessages from '../../../App/messages';
import tabMessages from '../messages';
import { HEADERS, NAME, PROPERTY_PATH_MAP } from './constants';
import { useRows } from './hooks';

type UseManageGrossRentCertificationsTableProps = {
  grossRentCertifications: GrossRentCertification[],
  refetch: () => void,
};

export const useManageGrossRentCertificationsTable = ({
  grossRentCertifications,
  refetch,
}: UseManageGrossRentCertificationsTableProps) => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const organizationId = selectedProperty?.organizationId ?? '';
  const propertyId = selectedProperty?.id ?? '';
  const name = useUniqueTableName(NAME);
  const modalProps = useModalProps();

  const {
    filterState,
    filterTypeState,
    dateState,
    order,
    orderBy,
    handleSortChange,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleDateSubmit,
    searchState,
  } = useTableFilterSortSearchManager({
    name,
    headers: HEADERS,
    initialOrderBy: 'unit',
    initialOrder: 'DESC',
  });

  const sortedAndFilteredCertifications = useTableFilterSortData({
    results: grossRentCertifications,
    order,
    orderBy,
    filterState,
    filterTypeState,
    searchState,
    dateState,
    PROPERTY_PATH_MAP,
  });

  // Ref: sync double-click guard. State: source of truth for row loading spinners.
  const generate59AInFlightRef = useRef<Set<string>>(new Set());
  const [generate59AInFlightIds, setGenerate59AInFlightIds] = useState<
    Array<string>,
  >([]);

  const publishGenerate59AInFlightIds = () => {
    setGenerate59AInFlightIds([...generate59AInFlightRef.current]);
  };

  const handleGenerate59A = useGenerate59AMutation({
    organizationId,
    propertyId,
    options: {
      enabled: Boolean(organizationId && propertyId),
      onSettled: (
        _data: mixed,
        _error: mixed,
        affordableQualificationId: string,
      ) => {
        generate59AInFlightRef.current.delete(affordableQualificationId);
        publishGenerate59AInFlightIds();
      },
      onSuccess: () => {
        toastr.success(
          intl.formatMessage(generalMessages.success),
          intl.formatMessage(tabMessages.generate59ASuccess),
        );
        refetch();
      },
      onError: (error: mixed) => {
        toastr.error(
          intl.formatMessage(generalMessages.error),
          intl.formatMessage(tabMessages.errorGenerating59A, {
            errorMessage: extractErrorMessage(error),
          }),
        );
      },
    },
  });

  const download59A = (affordableQualificationId: string) => {
    if (generate59AInFlightRef.current.has(affordableQualificationId)) {
      return;
    }
    generate59AInFlightRef.current.add(affordableQualificationId);
    publishGenerate59AInFlightIds();
    handleGenerate59A.mutate(affordableQualificationId);
  };

  const editGrossRentCertificationMutation =
    useEditGrossRentCertificationMutation({
      organizationId,
      propertyId,
      options: {
        enabled: Boolean(organizationId && propertyId),
        onSuccess: () => {
          toastr.success(
            'Success',
            'Successfully edited gross rent certification',
          );
          modalProps.handleClose();
          refetch();
        },
        onError: (error: mixed) => {
          toastr.error(
            'Error',
            `Failed to edit gross rent certification ${String(error)}`,
          );
        },
      },
    });

  const {
    certToCancel,
    hasCancelPermission,
    isCancelModalOpen,
    isSubmitting: isCancellingCertification,
    openCancelModal,
    closeCancelModal,
    confirmCancel,
  } = useCancelGrossRentCertificationAction();

  const rows = useRows({
    canCancelCertifications: hasCancelPermission,
    certifications: sortedAndFilteredCertifications,
    download59A,
    generate59AInFlightIds,
    modalProps,
    openCancelModal,
  });

  const {
    allColumnsHidden,
    columnOptions,
    filteredHeaders,
    selectedColumns,
    handleColumnChange,
    handleSettingsClose,
  } = useTableManageColumns({
    name,
    headers: HEADERS,
    firstRow: rows[0],
  });

  return {
    allColumnsHidden,
    cancelModalProps: {
      cert: certToCancel,
      isSubmitting: isCancellingCertification,
      onCancel: closeCancelModal,
      onConfirm: confirmCancel,
      open: isCancelModalOpen,
    },
    columnOptions,
    count: rows.length,
    dateState,
    editGrossRentCertificationMutation,
    filterState,
    filterTypeState,
    filteredHeaders,
    handleColumnChange,
    handleDateSubmit,
    handleFilterChange,
    handleFilterTypeChange,
    handleSearchSubmit,
    handleSettingsClose,
    handleSortChange,
    modalProps,
    name,
    order,
    orderBy,
    rows,
    searchState,
    selectedColumns,
    totalCount: grossRentCertifications.length,
  };
};
